import * as React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { IDialogProps } from '../../../../../components/form-dialog/dialog-props';
import { Button } from '../../../../../components/button';

const ariaLabelledBy = 'form-dialog-title';
const ariaDescribedBy = 'form-dialog-description';

export interface IFormDialogProps extends IDialogProps {
  onSecondaryConfirmClick?: () => void;
  secondaryConfirmButtonText?: string;
  isValid?: boolean;
  isLoading?: boolean;
  dialogContentClass?: string;
  secondaryConfirmIsPrimary?: boolean;
  fullWidth?: boolean;
  cancelIsPrimary?: boolean;
  disableBackdropClick?: boolean;
}

export function FormDialog({
  open,
  title,
  confirmButtonText,
  cancelButtonText,
  onCancelClick,
  onClose,
  onConfirmClick,
  isValid = true,
  isLoading = false,
  children,
  maxWidth = 'sm',
  dialogContentClass,
  extraDialogAction,
  fullWidth,
  cancelIsPrimary = false,
  disableBackdropClick,
  sx,
  onSecondaryConfirmClick,
  secondaryConfirmButtonText,
  secondaryConfirmIsPrimary = false,
}: React.PropsWithChildren<IFormDialogProps>) {
  function handleClose() {
    if (onClose) {
      onClose();
    } else if (onCancelClick) {
      onCancelClick();
    } else {
      onConfirmClick();
    }
  }

  function handleCancel() {
    if (onCancelClick) {
      onCancelClick();
    }
  }

  function handleSecondaryConfirm() {
    if (onSecondaryConfirmClick) {
      onSecondaryConfirmClick();
    }
  };

  return (
    <Dialog
      maxWidth={maxWidth}
      className="form-dialog"
      onClose={(event, reason) => {
        if ((reason === 'backdropClick' || reason === 'escapeKeyDown') && disableBackdropClick) {
          return;
        }
        handleClose();
      }}
      open={open}
      scroll="body"
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      fullWidth={fullWidth}
      sx={sx}
    >
      <DialogTitle id={ariaLabelledBy}>{title}</DialogTitle>
      <DialogContent className={dialogContentClass}>{children}</DialogContent>
      <DialogActions className="actions">
        {extraDialogAction}

        {/* Primary confirm */}
        <Button onClick={onConfirmClick} color="primary" variant={cancelIsPrimary ? 'outlined' : 'contained'} disabled={!isValid || isLoading}>
          {isLoading && (
            <CircularProgress
              size={24}
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                marginTop: -12,
                marginLeft: -12,
              }}
            />
          )}
          {confirmButtonText || 'Ok'}
        </Button>

        {/* Optional secondary confirm */}
        {onSecondaryConfirmClick && (
          <Button onClick={handleSecondaryConfirm} color="primary" variant={secondaryConfirmIsPrimary ? 'contained' : 'outlined'} disabled={!isValid || isLoading}>
            {isLoading && (
              <CircularProgress
                size={24}
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: -12,
                  marginLeft: -12,
                }}
              />
            )}
            {secondaryConfirmButtonText || 'Sekundær ok'}
          </Button>
        )}

        {/* Cancel */}
        {onCancelClick && (
          <Button onClick={handleCancel} color="primary" variant={cancelIsPrimary ? 'contained' : 'outlined'} disabled={isLoading}>
            {cancelButtonText || 'Annuller'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
